import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { SelectedPurposeOfUseOrContact } from '../../../common/enums.js';
import { purposeOfUseMsg, t, userMsg } from '../../../common/i18n/index.js';
import { SelectGroupItemWithId } from '../../SelectGroup/SelectGroupItem.js';
import { ContactOrPurposeSelectGroup } from './ContactOrPurposeSelectGroup.js';
import { CreateOrSelectContactForm } from './CreateOrSelectContactForm.js';
import { PurposeOfUseForm } from './PurposeOfUseForm.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { ContactOrPurposeOfUseFormValues } from '../ContactsOrPurposeOfUseUtils.js';

interface ContactOrPurposeOfUseSelectorProps {
  headerLevel?: 'h3' | 'h4' | 'h5';
  id: string;
  isEmailAndPhoneRequired?: boolean;
  required?: boolean;
  formattedContacts?: Contact[];
  onChangeContact?: (selectedContactId?: string) => void;
  onlyPurposeOfUse?: boolean;
}

export const ContactOrPurposeOfUseSelector = ({
  formattedContacts,
  isEmailAndPhoneRequired,
  onChangeContact,
  required,
  onlyPurposeOfUse,
}: ContactOrPurposeOfUseSelectorProps) => {
  const { watch, resetField } = useFormContext<ContactOrPurposeOfUseFormValues>();
  const selectionTab = watch('selectionTab');

  useEffect(() => {
    if (selectionTab === SelectedPurposeOfUseOrContact.CONTACT) {
      resetField('purposeOfUse', { defaultValue: '' });
    } else if (selectionTab === SelectedPurposeOfUseOrContact.PURPOSEOFUSE) {
      (['selectContact', 'firstName', 'lastName', 'phoneNumber', 'email'] as const).forEach(fieldId => {
        resetField(fieldId, { defaultValue: '' });
      });
    }
    if (onChangeContact) {
      onChangeContact(undefined);
    }
  }, [selectionTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const headingText = onlyPurposeOfUse ? t.NKJ8('User or purpose of use') : t.WPXQ('Select user or purpose of use');

  const formComponent = !onlyPurposeOfUse ? (
    <ContactOrPurposeSelectGroup
      className={`${dsClass.FONT_WEIGHT_600} ${dsClass.PADDING_0} ${dsClass.PADDING_BOTTOM_2} of-contact-or-purpose-of-use__select-group`}
      name="selectionTab"
      selectedItem={selectionTab}
    >
      <SelectGroupItemWithId title={t.LUZM(userMsg)} id={SelectedPurposeOfUseOrContact.CONTACT}>
        <CreateOrSelectContactForm
          contacts={formattedContacts}
          isEmailAndPhoneRequired={isEmailAndPhoneRequired}
          onChangeContact={onChangeContact}
          required={required}
        />
      </SelectGroupItemWithId>
      <SelectGroupItemWithId title={t.Y8KX(purposeOfUseMsg)} id={SelectedPurposeOfUseOrContact.PURPOSEOFUSE}>
        <PurposeOfUseForm required={required} />
      </SelectGroupItemWithId>
    </ContactOrPurposeSelectGroup>
  ) : (
    <PurposeOfUseForm required={required} />
  );

  return (
    <>
      <h5 className={`${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>{headingText}</h5>
      <p className={dsClass.MARGIN_VERTICAL_3}>
        {t.PV9I(
          'By adding user information, you can manage products and services easier in the OmaElisa Users view. Select the purpose of use only when it involves, for example, a group of users.'
        )}
      </p>
      {formComponent}
    </>
  );
};
